import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, AlertCircle } from 'lucide-react';
import { useIncidents } from '../hooks/useIncidents';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { DEGREE_LABELS, type IncidentDegree } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const getDegreeVariant = (degree: IncidentDegree): 'success' | 'default' | 'warning' | 'error' => {
  switch (degree) {
    case 'D0':
    case 'D1':
      return 'default';
    case 'D2':
      return 'warning';
    case 'D3':
    case 'D4':
    case 'D5':
      return 'error';
    default:
      return 'default';
  }
};

export function PendingApprovalsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const { data: incidentsData, isLoading } = useIncidents({
    page,
    limit: 20,
    status: 'PENDING_APPROVAL',
  });

  const incidents = incidentsData?.data ?? [];
  const pagination = incidentsData?.pagination;

  const filteredIncidents = useMemo(() => {
    if (!searchQuery) return incidents;

    const query = searchQuery.toLowerCase();
    return incidents.filter(
      (incident) =>
        incident.employee?.user.name.toLowerCase().includes(query) ||
        incident.employee?.user.email.toLowerCase().includes(query) ||
        incident.description.toLowerCase().includes(query) ||
        incident.category?.name.toLowerCase().includes(query)
    );
  }, [incidents, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <AlertCircle className="w-8 h-8 text-orange-500" />
        <div>
          <h1 className="text-3xl font-bold">Solicitações Pendentes</h1>
          <p className="text-muted-foreground">
            Advertências aguardando sua aprovação ({pagination?.total || 0})
          </p>
        </div>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar por colaborador, email ou descrição..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Incidentes Pendentes */}
      <div className="grid gap-4">
        {filteredIncidents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium text-muted-foreground mb-2">
                {searchQuery ? 'Nenhum resultado encontrado' : 'Nenhuma solicitação pendente'}
              </p>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Tente ajustar sua busca'
                  : 'Todas as advertências foram aprovadas ou rejeitadas'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredIncidents.map((incident) => (
            <Link key={incident.id} to={`/incident-management/${incident.id}`}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-orange-500">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">
                          {incident.employee?.user.name}
                        </CardTitle>
                        <Badge variant="warning">Aguardando Aprovação</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {incident.category?.name} • Registrado em{' '}
                        {format(new Date(incident.registeredAt), "dd 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })}
                        {' por '}
                        {incident.registeredBy?.name}
                      </p>

                      <div className="mb-3 p-3 bg-muted rounded-lg">
                        <p className="text-sm font-medium mb-1">Grau Sugerido pelo Sistema:</p>
                        <Badge variant={getDegreeVariant(incident.suggestedDegree)} className="text-base">
                          {DEGREE_LABELS[incident.suggestedDegree]}
                        </Badge>
                        {incident.previousDegree !== 'D0' && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Grau Anterior: {DEGREE_LABELS[incident.previousDegree]}
                          </p>
                        )}
                      </div>

                      <p className="text-foreground line-clamp-2">{incident.description}</p>

                      {incident.financialLoss && Number(incident.financialLoss) > 0 && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Prejuízo:</span>
                          <span className="text-sm font-bold text-red-600">
                            R$ {Number(incident.financialLoss).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>

                    <Button variant="default" className="ml-4">
                      <FileText className="w-4 h-4 mr-2" />
                      Analisar
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <span className="px-4 py-2">
            Página {page} de {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
